import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { identifyIap, logOutIap } from "@/lib/iap";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // If returning from OAuth callback, skip the /me check. AuthCallback will exchange the session_id.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Bridge user identity into RevenueCat (no-op on web / when SDK key missing).
  useEffect(() => {
    if (user?.user_id) {
      identifyIap(user.user_id);
    }
  }, [user?.user_id]);

  const loginWithJWT = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("auth_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const registerWithJWT = async (payload) => {
    const res = await api.post("/auth/register", payload);
    localStorage.setItem("auth_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { await logOutIap(); } catch {}
    localStorage.removeItem("auth_token");
    localStorage.removeItem("admin_token");
    setUser(null);
  };

  const startImpersonation = async (targetUserId) => {
    const res = await api.post(`/admin/impersonate/${targetUserId}`);
    // Save the admin's own token so we can restore it
    const currentToken = localStorage.getItem("auth_token");
    if (currentToken) localStorage.setItem("admin_token", currentToken);
    localStorage.setItem("auth_token", res.data.token);
    setUser({ ...res.data.user, impersonator_id: res.data.impersonator?.user_id });
    return res.data.user;
  };

  const stopImpersonation = async () => {
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      localStorage.setItem("auth_token", adminToken);
      localStorage.removeItem("admin_token");
    }
    await checkAuth();
  };

  const setRole = async (payload) => {
    const res = await api.post("/auth/role", payload);
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, loginWithJWT, registerWithJWT, logout, setRole, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
